import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { ThemeProvider } from "./lib/theme";
import { I18nProvider, useI18n } from "./lib/i18n";
import ErrorBoundary from "./components/ErrorBoundary";

// Pages
import LandingPage from "./pages/LandingPage";
import PatientLandingPage from "./pages/PatientLandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AuthCallback from "./pages/AuthCallback";
import TherapistDashboard from "./pages/TherapistDashboard";
import PatientDashboard from "./pages/PatientDashboard";
import PatientsWorkspacePage from "./pages/PatientsWorkspacePage";
import ExerciseLibrary from "./pages/ExerciseLibrary";
import MoodTracker from "./pages/MoodTracker";
import EmotionRegisterPage from "./pages/EmotionRegisterPage";
import GratitudePage from "./pages/GratitudePage";
import PomodoroPage from "./pages/PomodoroPage";
import MeditationPage from "./pages/MeditationPage";
import CognitiveDefusionPage from "./pages/CognitiveDefusionPage";
import SelfCompassionPage from "./pages/SelfCompassionPage";
import SelfReinforcementPage from "./pages/SelfReinforcementPage";
import BehavioralActivationPage from "./pages/BehavioralActivationPage";
import RemindersDashboard from "./pages/RemindersDashboard";
import ValuesExplorationPage from "./pages/ValuesExplorationPage";
import NeedsExplorationPage from "./pages/NeedsExplorationPage";
import ProblemSolvingPage from "./pages/ProblemSolvingPage";
import QualitiesExplorationPage from "./pages/QualitiesExplorationPage";
import CapabilitiesExplorationPage from "./pages/CapabilitiesExplorationPage";
import AssertivenessPage from "./pages/AssertivenessPage";
import CognitiveRestructuringPage from "./pages/CognitiveRestructuringPage";
import ProfilePage from "./pages/ProfilePage";
import PricingPage from "./pages/PricingPage";
import PatientExercisesPage from "./pages/PatientExercisesPage";
import BarkleyExercisePage from "./pages/BarkleyExercisePage";
import PsychoeducationPage from "./pages/PsychoeducationPage";
import TheoryModulesPage from "./pages/TheoryModulesPage";
import BadgesPage from "./pages/BadgesPage";
import MorningChecklistPage from "./pages/MorningChecklistPage";
import TdahKidsExercisePage from "./pages/TdahKidsExercisePage";
import ParentTaskBoardPage from "./pages/ParentTaskBoardPage";
import SubscriptionSuccessPage from "./pages/SubscriptionSuccessPage";
import SchemaExercisesPage from "./pages/SchemaExercisesPage";
import TcdExercisesPage from "./pages/TcdExercisesPage";
import DissociationExercisesPage from "./pages/DissociationExercisesPage";
import SexualityExercisesPage from "./pages/SexualityExercisesPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsPage from "./pages/TermsPage";
import CGVPage from "./pages/CGVPage";
import SessionReportsPage from "./pages/SessionReportsPage";
import PatientExerciseReview from "./pages/PatientExerciseReview";
import ConsentBanner from "./components/ConsentBanner";
import CharterPage from "./pages/CharterPage";
import TherapistMeditationsPage from "./pages/TherapistMeditationsPage";
import PatientMeditationsPage from "./pages/PatientMeditationsPage";
import TwoFactorSetupPage from "./pages/TwoFactorSetupPage";
import TwoFactorVerifyPage from "./pages/TwoFactorVerifyPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import StatisticsPage from "./pages/StatisticsPage";
import DersQuestionnairePage from "./pages/DersQuestionnairePage";
import DersResultsPage from "./pages/DersResultsPage";
import PerfectionismQuestionnairePage from "./pages/PerfectionismQuestionnairePage";
import PerfectionismResultsPage from "./pages/PerfectionismResultsPage";
import GriefQuestionnairePage from "./pages/GriefQuestionnairePage";
import GriefResultsPage from "./pages/GriefResultsPage";
import AdminDashboard from "./pages/admin/AdminDashboard";
import { registerPushNotifications } from "./lib/push";
import UpdateBanner from "./components/UpdateBanner";

// ALWAYS use current domain as backend URL (frontend and backend are on same domain)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
export const API = `${BACKEND_URL}/api`;

// Configure axios to always send session token
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.withCredentials = true;
  return config;
});

// Auth context
export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    const delays = [1500, 3000, 6000];
    for (let attempt = 0; ; attempt++) {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        localStorage.setItem('cached_user', JSON.stringify(response.data));
        break;
      } catch (error) {
        const status = error.response?.status;
        if (status === 401 || status === 403) {
          // Session réellement invalide : on déconnecte
          localStorage.removeItem('session_token');
          localStorage.removeItem('cached_user');
          setUser(null);
          break;
        }
        // Backend injoignable (déploiement, réseau) : on réessaie puis on garde la session connue
        if (attempt < delays.length) { await new Promise((r) => setTimeout(r, delays[attempt])); continue; }
        const cached = localStorage.getItem('cached_user');
        setUser(cached && localStorage.getItem('session_token') ? JSON.parse(cached) : null);
        break;
      }
    }
    setLoading(false);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (error) {
      console.error("Logout error:", error);
    }
    localStorage.removeItem('session_token');
    localStorage.removeItem('cached_user');
    setUser(null);
  };

  useEffect(() => {
    checkAuth();
  }, []);

  // Register push notifications when user is logged in
  useEffect(() => {
    if (user && user.role === "patient") {
      registerPushNotifications().catch(console.error);
    }
  }, [user]);

  // Auto-logout: 3h for patients, 2h for therapists
  useEffect(() => {
    if (!user) return;

    const INACTIVITY_MS = user.role === "therapist" ? 2 * 60 * 60 * 1000 : 3 * 60 * 60 * 1000;
    let timer;

    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        logout();
        window.location.href = "/login?reason=inactivity";
      }, INACTIVITY_MS);
    };

    const events = ["mousedown", "keydown", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();

    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, resetTimer));
    };
  }, [user]);

  return { user, setUser, loading, logout, checkAuth };
};

// Protected Route component
const ProtectedRoute = ({ children, user, loading, allowedRoles }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // If user data was passed from AuthCallback via location state, use it
  useEffect(() => {
    if (location.state?.user && !user) {
      // User just authenticated, redirect will happen naturally
    }
  }, [location.state, user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-primary/20"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if patient is active
  if (user.role === "patient" && !user.is_active) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-md text-center">
          <h1 className="font-heading text-2xl mb-4">Account Pending Activation</h1>
          <p className="text-muted-foreground mb-6">
            Your account is waiting for activation by your therapist. Please contact them for access.
          </p>
          <button
            onClick={() => navigate("/login")}
            className="text-primary hover:underline"
          >
            Return to login
          </button>
        </div>
      </div>
    );
  }

  // Solo patient subscription redirect disabled — flow removed
  // Patients are always allowed to access their dashboard

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={user.role === "therapist" ? "/therapist" : "/patient"} replace />;
  }

  return children;
};

// Main App Router
function AppRouter() {
  const { user, setUser, loading, logout, checkAuth } = useAuth();
  const location = useLocation();
  const { setLocale } = useI18n();

  // Applique une fois par compte la langue enregistrée sur la fiche patient
  useEffect(() => {
    if (!user?.user_id || user.role !== "patient" || !user.language) return;
    if (localStorage.getItem("app_locale_applied_for") === user.user_id) return;
    setLocale(user.language);
    localStorage.setItem("app_locale_applied_for", user.user_id);
  }, [user?.user_id, user?.role, user?.language]);

  // CRITICAL: Check URL for session_id synchronously during render
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback setUser={setUser} />;
  }

  return (
    <>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/bienvenue" element={<PatientLandingPage />} />
        <Route path="/login" element={<LoginPage setUser={setUser} />} />
        <Route path="/register" element={<RegisterPage setUser={setUser} />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Therapist routes */}
        <Route
          path="/therapist"
          element={
            <ProtectedRoute user={user} loading={loading} allowedRoles={["therapist"]}>
              <TherapistDashboard user={user} logout={logout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/therapist/patients"
          element={
            <ProtectedRoute user={user} loading={loading} allowedRoles={["therapist"]}>
              <PatientsWorkspacePage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/therapist/patients/:patientId"
          element={
            <ProtectedRoute user={user} loading={loading} allowedRoles={["therapist"]}>
              <PatientsWorkspacePage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/therapist/patients/:patientId/exercises"
          element={
            <ProtectedRoute user={user} loading={loading} allowedRoles={["therapist"]}>
              <PatientExercisesPage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/therapist/patients/:patientId/exercise-review"
          element={
            <ProtectedRoute user={user} loading={loading} allowedRoles={["therapist"]}>
              <PatientExerciseReview user={user} logout={logout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/therapist/exercises"
          element={
            <ProtectedRoute user={user} loading={loading} allowedRoles={["therapist"]}>
              <ExerciseLibrary user={user} logout={logout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/therapist/reminders"
          element={
            <ProtectedRoute user={user} loading={loading} allowedRoles={["therapist"]}>
              <RemindersDashboard user={user} logout={logout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/therapist/session-reports"
          element={
            <ProtectedRoute user={user} loading={loading} allowedRoles={["therapist"]}>
              <SessionReportsPage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/therapist/my-exercises"
          element={
            <ProtectedRoute user={user} loading={loading} allowedRoles={["therapist"]}>
              <PatientExercisesPage user={user} logout={logout} selfView />
            </ProtectedRoute>
          }
        />
        <Route
          path="/therapist/statistics"
          element={
            <ProtectedRoute user={user} loading={loading} allowedRoles={["therapist"]}>
              <StatisticsPage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />
        <Route path="/therapist/messaging" element={<Navigate to="/profile#messaging" replace />} />
        <Route
          path="/therapist/patients/:patientId/perfectionism"
          element={
            <ProtectedRoute user={user} loading={loading} allowedRoles={["therapist"]}>
              <PerfectionismResultsPage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/exercises/perfectionism-scale"
          element={
            <ProtectedRoute user={user} loading={loading} allowedRoles={["patient", "therapist"]}>
              <PerfectionismQuestionnairePage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/therapist/patients/:patientId/grief"
          element={
            <ProtectedRoute user={user} loading={loading} allowedRoles={["therapist"]}>
              <GriefResultsPage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/exercises/grief-scale"
          element={
            <ProtectedRoute user={user} loading={loading} allowedRoles={["patient", "therapist"]}>
              <GriefQuestionnairePage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/therapist/patients/:patientId/ders"
          element={
            <ProtectedRoute user={user} loading={loading} allowedRoles={["therapist"]}>
              <DersResultsPage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/therapist/meditations"
          element={
            <ProtectedRoute user={user} loading={loading} allowedRoles={["therapist"]}>
              <TherapistMeditationsPage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />

        {/* Patient routes */}
        <Route
          path="/patient"
          element={
            <ProtectedRoute user={user} loading={loading} allowedRoles={["patient"]}>
              <PatientDashboard user={user} logout={logout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/patient/my-exercises"
          element={
            <ProtectedRoute user={user} loading={loading} allowedRoles={["patient"]}>
              <PatientExercisesPage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/patient/meditations"
          element={
            <ProtectedRoute user={user} loading={loading} allowedRoles={["patient"]}>
              <PatientMeditationsPage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/patient/ders"
          element={
            <ProtectedRoute user={user} loading={loading} allowedRoles={["patient"]}>
              <DersQuestionnairePage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />
        
        {/* Shared exercise routes */}
        <Route
          path="/exercises/mood"
          element={
            <ProtectedRoute user={user} loading={loading}>
              <MoodTracker user={user} logout={logout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/exercises/emotion-register"
          element={
            <ProtectedRoute user={user} loading={loading}>
              <EmotionRegisterPage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/exercises/gratitude"
          element={
            <ProtectedRoute user={user} loading={loading}>
              <GratitudePage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/exercises/pomodoro"
          element={
            <ProtectedRoute user={user} loading={loading}>
              <PomodoroPage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/exercises/meditation"
          element={
            <ProtectedRoute user={user} loading={loading}>
              <MeditationPage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/exercises/cognitive-defusion"
          element={
            <ProtectedRoute user={user} loading={loading}>
              <CognitiveDefusionPage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/exercises/self-compassion"
          element={
            <ProtectedRoute user={user} loading={loading}>
              <SelfCompassionPage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/exercises/behavioral-activation"
          element={
            <ProtectedRoute user={user} loading={loading}>
              <BehavioralActivationPage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/exercises/values-exploration"
          element={
            <ProtectedRoute user={user} loading={loading}>
              <ValuesExplorationPage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/exercises/needs-exploration"
          element={
            <ProtectedRoute user={user} loading={loading}>
              <NeedsExplorationPage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/exercises/problem-solving"
          element={
            <ProtectedRoute user={user} loading={loading}>
              <ProblemSolvingPage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/exercises/qualities-exploration"
          element={
            <ProtectedRoute user={user} loading={loading}>
              <QualitiesExplorationPage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/exercises/capabilities-exploration"
          element={
            <ProtectedRoute user={user} loading={loading}>
              <CapabilitiesExplorationPage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/exercises/assertiveness"
          element={
            <ProtectedRoute user={user} loading={loading}>
              <AssertivenessPage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/exercises/self-reinforcement"
          element={
            <ProtectedRoute user={user} loading={loading}>
              <SelfReinforcementPage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/exercises/cognitive-restructuring"
          element={
            <ProtectedRoute user={user} loading={loading}>
              <CognitiveRestructuringPage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />

        {/* Barkley Exercises */}
        <Route
          path="/exercises/barkley/:exerciseType"
          element={
            <ProtectedRoute user={user} loading={loading}>
              <BarkleyExercisePage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />

        {/* ACT Exercises */}
        <Route
          path="/exercises/act/:exerciseType"
          element={
            <ProtectedRoute user={user} loading={loading}>
              <BarkleyExercisePage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />

        {/* TDAH Adulte Exercises */}
        <Route
          path="/exercises/tdah/:exerciseType"
          element={
            <ProtectedRoute user={user} loading={loading}>
              <BarkleyExercisePage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />

        {/* Psychoeducation - New modules */}
        <Route
          path="/exercises/psychoed/:exerciseType"
          element={
            <ProtectedRoute user={user} loading={loading}>
              <PsychoeducationPage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />

        {/* Schema Therapy Interactive Exercises */}
        <Route
          path="/exercises/schema-therapy"
          element={
            <ProtectedRoute user={user} loading={loading}>
              <SchemaExercisesPage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />

        {/* TCD Interactive Exercises */}
        <Route
          path="/exercises/tcd-exercises"
          element={
            <ProtectedRoute user={user} loading={loading}>
              <TcdExercisesPage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />

        {/* Dissociation & Trauma Interactive Exercises */}
        <Route
          path="/exercises/dissociation-exercises"
          element={
            <ProtectedRoute user={user} loading={loading}>
              <DissociationExercisesPage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />

        {/* TCC Sexuality Exercises */}
        <Route
          path="/exercises/sexuality-exercises"
          element={
            <ProtectedRoute user={user} loading={loading}>
              <SexualityExercisesPage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />

        {/* TDAH Kids Workbook Exercises */}
        <Route
          path="/exercises/tdah-kids/:exerciseType"
          element={
            <ProtectedRoute user={user} loading={loading}>
              <TdahKidsExercisePage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />

        {/* Parent Task Badge Board */}
        <Route
          path="/exercises/parent-task-board"
          element={
            <ProtectedRoute user={user} loading={loading}>
              <ParentTaskBoardPage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />

        {/* Morning Checklist */}
        <Route
          path="/exercises/morning-checklist"
          element={
            <ProtectedRoute user={user} loading={loading}>
              <MorningChecklistPage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />

        {/* Badges (children) */}
        <Route
          path="/patient/badges"
          element={
            <ProtectedRoute user={user} loading={loading} allowedRoles={["patient"]}>
              <BadgesPage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />

        {/* Theory Models (Modeles theoriques) */}
        <Route
          path="/psychoeducation"
          element={
            <ProtectedRoute user={user} loading={loading}>
              <TheoryModulesPage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />

        {/* 2FA routes (public - pre-login) */}
        <Route path="/2fa/setup" element={<TwoFactorSetupPage setUser={setUser} />} />
        <Route path="/2fa/verify" element={<TwoFactorVerifyPage setUser={setUser} />} />

        {/* Profile */}
        <Route
          path="/profile"
          element={
            <ProtectedRoute user={user} loading={loading}>
              <ProfilePage user={user} logout={logout} checkAuth={checkAuth} />
            </ProtectedRoute>
          }
        />

        {/* Pricing & Subscription */}
        <Route
          path="/pricing"
          element={
            <ProtectedRoute user={user} loading={loading}>
              <PricingPage user={user} logout={logout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/subscription/success"
          element={
            <ProtectedRoute user={user} loading={loading}>
              <SubscriptionSuccessPage user={user} />
            </ProtectedRoute>
          }
        />

        {/* Admin */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute user={user} loading={loading} allowedRoles={["therapist"]}>
              <AdminDashboard user={user} logout={logout} />
            </ProtectedRoute>
          }
        />

        {/* Legal */}
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/cgv" element={<CGVPage />} />
        <Route path="/charter" element={<CharterPage />} />

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <ConsentBanner user={user} />
      <UpdateBanner />
      <Toaster position="top-right" />
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <I18nProvider>
        <ErrorBoundary>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
        </ErrorBoundary>
      </I18nProvider>
    </ThemeProvider>
  );
}

export default App;
